import { mailTemplateSampleValues } from "@/assets/mock/mailConfigMock";
import { EmailTemplate } from "@/types";
import { api } from "@/auth/api";
import { settings } from "@/settings";
import { deepClone, headers } from "@/utils";

const systemUid = "odm-contactmechanism";
const emailTemplateCollectionUrl = `${settings.api_url}/${systemUid}/api/1/model/${systemUid}/emailtemplate/`;
const emailTemplateUrl = (resourceUid: string) =>
  `${emailTemplateCollectionUrl}${resourceUid}/`;

const HTML_TAG_RE = /<\/?[a-z][\s\S]*>/i;

function decodeMailEscapeNewlines(value: string): string {
  return value
    .replace(/\\r\\n/gi, "\n")
    .replace(/\\n/gi, "\n")
    .replace(/\\r/gi, "\n");
}

function newlinesToBr(value: string): string {
  return decodeMailEscapeNewlines(value).replace(/\r\n|\r|\n/g, "<br/>");
}

export function applyMailPreviewSamples(value?: string | null): string {
  if (value == null || value === "") {
    return "";
  }
  return String(value).replace(/\$\{([^}]+)\}/g, (_match, key: string) => {
    return mailTemplateSampleValues[key] ?? `\${${key}}`;
  });
}

export function buildMailPreviewSrcDoc(body?: string | null): string {
  const html = newlinesToBr(applyMailPreviewSamples(toMailEditorHtml(body)));
  const emptyMessage =
    '<p style="margin:0;color:#64748b;font-family:Arial,Helvetica,sans-serif;">&nbsp;</p>';
  const content = html || emptyMessage;
  if (/<html[\s>]/i.test(content)) {
    return content;
  }
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <meta name="viewport" content="width=device-width, initial-scale=1"/>
  <style>
    html, body { margin: 0; padding: 0; background: #fff; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 14px;
      line-height: 1.6;
      color: #202124;
      padding: 4px 2px 12px;
      word-break: break-word;
    }
    a { color: #1a73e8; }
    img { max-width: 100%; height: auto; }
    p { margin: 0 0 0.75em; }
  </style>
</head>
<body>${content}</body>
</html>`;
}

export function isMailBodyEmpty(html?: string | null): boolean {
  const text = (html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return !text;
}

export function toMailEditorHtml(body?: string | null): string {
  if (body == null || body === "") {
    return "";
  }
  const withRealNewlines = decodeMailEscapeNewlines(String(body));
  if (HTML_TAG_RE.test(withRealNewlines)) {
    return withRealNewlines
      .replace(/\r\n|\r|\n/g, "<br/>")
      .replace(/<br\s*\/?>/gi, "<br/>");
  }
  return withRealNewlines
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\r\n|\r|\n/g, "<br/>");
}

export function toMailPersistedHtml(html?: string | null): string {
  if (!html || isMailBodyEmpty(html)) {
    return "";
  }
  let out = html.trim();
  out = out.replace(/<br\s*\/?>/gi, "<br/>");
  out = out.replace(/<div>(<br\s*\/?>)?<\/div>/gi, "<br/>");
  out = out.replace(/<p>(<br\s*\/?>)?<\/p>/gi, "<br/>");
  out = out.replace(/<div>([\s\S]*?)<\/div>/gi, "$1<br/>");
  out = out.replace(/<p>([\s\S]*?)<\/p>/gi, "$1<br/>");
  out = out.replace(/(<br\s*\/?>\s*)+$/gi, "");
  return out.trim();
}

function firstNonEmpty(...values: any[]): string {
  for (const value of values) {
    if (value != null && String(value).trim() !== "") {
      return String(value);
    }
  }
  return "";
}

export function getEmptyMailTemplate(): EmailTemplate {
  return {
    resourceUid: "",
    objectType: "odm.contactmechanism.emailtemplate",
    systemUid: "odm-contactmechanism",
    businessData: null,
    daaq: "/",
    sender: "",
    recipient: "",
    copyRecipient: "",
    hiddenRecipient: "",
    subject: "",
    body: "",
  };
}

export function mapToEmailTemplate(raw: any): EmailTemplate {
  if (!raw) {
    return getEmptyMailTemplate();
  }
  const mapped = getEmptyMailTemplate();
  mapped.resourceUid = firstNonEmpty(raw.resourceUid, raw.id);
  mapped.objectType = raw.objectType || mapped.objectType;
  mapped.systemUid = raw.systemUid || mapped.systemUid;
  mapped.businessData = raw.businessData ?? mapped.businessData;
  mapped.daaq = raw.daaq ?? raw._daaq ?? "/";
  mapped.sender = firstNonEmpty(raw.sender);
  mapped.recipient = firstNonEmpty(raw.recipient);
  mapped.copyRecipient = firstNonEmpty(raw.copyRecipient, raw.copy_recipient) || null;
  mapped.hiddenRecipient = firstNonEmpty(raw.hiddenRecipient, raw.hidden_recipient) || null;
  mapped.subject = firstNonEmpty(raw.subject, raw._subject);
  mapped.body = firstNonEmpty(raw.body);
  mapped._when_created = raw._when_created;
  mapped._when_modify = raw._when_modify || raw._when_modified;
  mapped._who_created = raw._who_created;
  mapped._who_modify = raw._who_modify || raw._who_modified;
  return mapped;
}

export function cloneMailAsNew(raw: any): EmailTemplate {
  const copy = mapToEmailTemplate(raw);
  copy.resourceUid = "";
  copy.body = toMailEditorHtml(copy.body);
  copy.copyRecipient = copy.copyRecipient || "";
  copy.hiddenRecipient = copy.hiddenRecipient || "";
  delete copy._when_created;
  delete copy._when_modify;
  delete copy._who_created;
  delete copy._who_modify;
  return copy;
}

function hasMailFilters(filter: any = {}) {
  return !!(
    filter?.sender ||
    filter?.recipient ||
    filter?.subject ||
    filter?.resourceUid
  );
}

function matchesLike(value: string | null | undefined, filter?: string): boolean {
  if (!filter) {
    return true;
  }
  return (value || "").toLowerCase().includes(String(filter).toLowerCase());
}

function buildMailTemplatesQc(offset = 0, limit = 10) {
  return {
    queryId: "mail-templates",
    offset: offset || 0,
    limit: limit || 10,
  };
}

function extractQueryRows(payload: any): any[] {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }
  if (Array.isArray(payload?.items)) {
    return payload.items;
  }
  return [];
}

function extractQueryCount(payload: any, fallback: number): number {
  const count = payload?.count ?? payload?.total ?? payload;
  const parsed = Number(count);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export async function queryMailTemplates(
  filter: any = {},
  offset = 0,
  limit = 10
): Promise<{ data: EmailTemplate[]; count: number }> {
  const filteredSearch = hasMailFilters(filter);
  const qc = buildMailTemplatesQc(
    filteredSearch ? 0 : offset,
    filteredSearch ? 1000 : limit
  );
  const qcHeader = headers({ qc: JSON.stringify(qc) });

  const listResponse = await api().get(settings.api_query_url, {
    headers: qcHeader,
  });
  let rows = extractQueryRows(listResponse.data).map(mapToEmailTemplate);

  if (filteredSearch) {
    rows = rows.filter(
      (item) =>
        matchesLike(item.sender, filter.sender) &&
        matchesLike(item.recipient, filter.recipient) &&
        matchesLike(item.subject, filter.subject) &&
        matchesLike(item.resourceUid, filter.resourceUid)
    );
    return {
      data: rows.slice(offset, offset + limit),
      count: rows.length,
    };
  }

  let count = rows.length;
  try {
    const countResponse = await api().get(settings.api_query_count_url, {
      headers: qcHeader,
    });
    count = extractQueryCount(countResponse.data, rows.length);
  } catch {
    count = offset + rows.length;
  }

  return { data: rows, count };
}

function buildEmailTemplatePayload(mail: EmailTemplate): EmailTemplate {
  const next: EmailTemplate = deepClone(mail) || getEmptyMailTemplate();
  next.objectType = next.objectType || "odm.contactmechanism.emailtemplate";
  next.systemUid = next.systemUid || systemUid;
  next.daaq = next.daaq ?? "/";
  next.copyRecipient = next.copyRecipient || null;
  next.hiddenRecipient = next.hiddenRecipient || null;
  next.body = toMailPersistedHtml(next.body);
  return next;
}

export async function getMailTemplate(resourceUid: string): Promise<EmailTemplate> {
  const response = await api().get(emailTemplateUrl(resourceUid));
  const mail = mapToEmailTemplate(response.data);
  mail.body = toMailEditorHtml(mail.body);
  return mail;
}

export async function createMailTemplate(mail: EmailTemplate): Promise<EmailTemplate> {
  const payload = buildEmailTemplatePayload(mail);
  if (!payload.resourceUid) {
    delete payload.resourceUid;
  }
  const response = await api().post(emailTemplateCollectionUrl, payload);
  const saved = mapToEmailTemplate(response.data || payload);
  saved.body = toMailEditorHtml(saved.body);
  return saved;
}

export async function updateMailTemplate(mail: EmailTemplate): Promise<EmailTemplate> {
  const payload = buildEmailTemplatePayload(mail);
  if (!payload.resourceUid) {
    throw new Error("resourceUid is required to update an email template");
  }
  const response = await api().put(emailTemplateUrl(payload.resourceUid), payload);
  const saved = mapToEmailTemplate(response.data || payload);
  saved.body = toMailEditorHtml(saved.body);
  return saved;
}
